import React, { useCallback, useEffect, useRef, useState } from 'react';
import {
  Box,
  CircularProgress,
  IconButton,
  InputAdornment,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import { OpenInNew } from '@mui/icons-material';
import { Autocomplete as GooglePlacesAutocomplete, GoogleMap, Marker } from '@react-google-maps/api';
import { useTranslation } from 'react-i18next';
import { useGoogleMaps } from '../../contexts/GoogleMapsContext';
import {
  applyGooglePlaceToPlaceAddressDraft,
  hasPlaceLocationSelected,
  type PlaceAddressDraft,
} from '../../utils/placeAddress';

const DEFAULT_MAP_CENTER = { lat: 52.2297, lng: 21.0122 };

export interface PlaceAddressFieldErrors {
  placeLocation?: string;
  addressLine1?: string;
}

export interface PlaceLocationAddressFieldsProps {
  value: PlaceAddressDraft;
  onChange: (value: PlaceAddressDraft) => void;
  showValidation?: boolean;
  fieldErrors?: PlaceAddressFieldErrors | null;
  title?: string;
  subtitle?: string;
  compact?: boolean;
}

const PlaceLocationAddressFields: React.FC<PlaceLocationAddressFieldsProps> = ({
  value,
  onChange,
  showValidation = false,
  fieldErrors,
  title,
  subtitle,
  compact = false,
}) => {
  const { t } = useTranslation();
  const { isLoaded, loadError } = useGoogleMaps();
  const [selectedPlace, setSelectedPlace] = useState<google.maps.places.PlaceResult | null>(null);
  const [mapCenter, setMapCenter] = useState<google.maps.LatLngLiteral>(DEFAULT_MAP_CENTER);
  const [hasInteracted, setHasInteracted] = useState(false);
  const autocompleteRef = useRef<google.maps.places.Autocomplete | null>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  const placeSelected = selectedPlace != null || hasPlaceLocationSelected(value);
  const showDetailFields = placeSelected;

  useEffect(() => {
    if (value.latitude != null && value.longitude != null) {
      setMapCenter({ lat: value.latitude, lng: value.longitude });
    }
  }, [value.latitude, value.longitude]);

  useEffect(() => {
    if (!isLoaded || !inputRef.current) return;
    const display = value.location?.trim() || [value.addressLine1, value.city].filter(Boolean).join(', ');
    if (display && !inputRef.current.value) {
      inputRef.current.value = display;
    }
  }, [isLoaded, value.location, value.addressLine1, value.city]);

  const onPlaceSelected = useCallback(
    (place: google.maps.places.PlaceResult) => {
      setSelectedPlace(place);
      setHasInteracted(true);
      const updated = applyGooglePlaceToPlaceAddressDraft(place, value);
      if (updated) {
        onChange(updated);
        if (place.geometry?.location) {
          setMapCenter({
            lat: place.geometry.location.lat(),
            lng: place.geometry.location.lng(),
          });
        }
      }
    },
    [onChange, value]
  );

  const onLoad = useCallback((autocomplete: google.maps.places.Autocomplete) => {
    autocompleteRef.current = autocomplete;
    autocomplete.setFields(['address_components', 'geometry', 'formatted_address', 'url', 'place_id', 'name']);
  }, []);

  const onPlaceChanged = useCallback(() => {
    if (autocompleteRef.current) {
      onPlaceSelected(autocompleteRef.current.getPlace());
    }
  }, [onPlaceSelected]);

  const updateField = (field: keyof PlaceAddressDraft, fieldValue: string) => {
    onChange({ ...value, [field]: fieldValue });
  };

  const placeLocationError =
    fieldErrors?.placeLocation ??
    (showValidation && !placeSelected
      ? t('space.placeLocationRequired', 'Please select a location from Google Maps')
      : undefined);

  if (loadError) {
    return (
      <Typography variant="body2" color="error">
        {t('common.errorLoadingMaps', 'Failed to load maps')}
      </Typography>
    );
  }

  if (!isLoaded) {
    return (
      <Box display="flex" justifyContent="center" py={2}>
        <CircularProgress size={28} />
      </Box>
    );
  }

  return (
    <Stack spacing={compact ? 1 : 1.5}>
      {title ? (
        <Typography variant={compact ? 'overline' : 'subtitle2'} color={compact ? 'text.secondary' : 'text.primary'}>
          {title}
        </Typography>
      ) : null}
      {subtitle ? (
        <Typography variant="body2" color="text.secondary">
          {subtitle}
        </Typography>
      ) : null}

      <GooglePlacesAutocomplete onLoad={onLoad} onPlaceChanged={onPlaceChanged}>
        <TextField
          inputRef={inputRef}
          label={t('space.placeLocation', 'Find in Google Maps')}
          fullWidth
          size={compact ? 'small' : 'medium'}
          required
          error={Boolean(placeLocationError)}
          helperText={placeLocationError}
          onFocus={() => setHasInteracted(true)}
        />
      </GooglePlacesAutocomplete>

      <Box sx={{ height: compact ? 120 : { xs: 200, sm: 300 }, width: '100%' }}>
        <GoogleMap mapContainerStyle={{ width: '100%', height: '100%' }} center={mapCenter} zoom={15}>
          {(selectedPlace?.geometry?.location || (value.latitude != null && value.longitude != null)) && (
            <Marker
              position={
                selectedPlace?.geometry?.location
                  ? {
                      lat: selectedPlace.geometry.location.lat(),
                      lng: selectedPlace.geometry.location.lng(),
                    }
                  : { lat: value.latitude!, lng: value.longitude! }
              }
            />
          )}
        </GoogleMap>
      </Box>

      {showDetailFields ? (
        <>
          <Box
            sx={{
              display: 'grid',
              gap: compact ? 1 : 1.5,
              gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr 1fr' },
            }}
          >
            <TextField label={t('space.city', 'City')} value={value.city} fullWidth size={compact ? 'small' : 'medium'} disabled />
            <TextField label={t('space.state', 'State')} value={value.state} fullWidth size={compact ? 'small' : 'medium'} disabled />
            <TextField label={t('space.zipCode', 'Zip Code')} value={value.zipCode} fullWidth size={compact ? 'small' : 'medium'} disabled />
          </Box>

          <Box
            sx={{
              display: 'grid',
              gap: compact ? 1 : 1.5,
              gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' },
            }}
          >
            <TextField
              label={t('space.addressLine1', 'Address Line 1')}
              value={value.addressLine1}
              onChange={(e) => updateField('addressLine1', e.target.value)}
              fullWidth
              size={compact ? 'small' : 'medium'}
              required
              error={Boolean(fieldErrors?.addressLine1)}
              helperText={fieldErrors?.addressLine1 ?? (compact ? undefined : ' ')}
            />
            <TextField
              label={t('space.addressLine2', 'Address Line 2')}
              value={value.addressLine2}
              onChange={(e) => updateField('addressLine2', e.target.value)}
              fullWidth
              size={compact ? 'small' : 'medium'}
            />
          </Box>

          <TextField
            label={t('space.googleMapsURL', 'Google Maps URL')}
            value={value.googleMapsUrl}
            fullWidth
            size={compact ? 'small' : 'medium'}
            required
            type="url"
            onChange={(e) => updateField('googleMapsUrl', e.target.value)}
            InputProps={{
              endAdornment: value.googleMapsUrl ? (
                <InputAdornment position="end">
                  <IconButton edge="end" onClick={() => window.open(value.googleMapsUrl, '_blank')} size="small">
                    <OpenInNew />
                  </IconButton>
                </InputAdornment>
              ) : undefined,
            }}
          />
        </>
      ) : null}
    </Stack>
  );
};

export default PlaceLocationAddressFields;
